/**
 * @fileoverview Loans & Advances hub page.
 * @description Two clickable cards linking to this group's sub-pages (Product-wise Loan
 * Portfolio, Interest Rate-wise Loan Portfolio) — two cuts of the same loan book. Ported from the
 * InsightBox Banking design system's own `LoansAdvances/Grid.tsx`; see `BankingMembershipPage.tsx`
 * for the shared local `fy`/`branchId` filter-state convention.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  LATEST_FY,
  core,
  formatSignedPct,
  loanProductMix,
  loanRateSlabs,
  resolveBankingAccessScope,
  resolveFy,
  trendLabel,
  useBankingData,
  yoyGrowth,
} from '@/features/banking'
import PageHeader from '@/shared/components/molecules/PageHeader'
import ViewGridCard from '@/shared/components/molecules/ViewGridCard'
import { ROUTES } from '@/shared/constants/routes.constants'

import type { FyFilter } from '@/features/banking'

/**
 * @component
 */
function BankingLoansPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter] = useState<FyFilter>(LATEST_FY)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.loans.kicker')}
      title={t('nav.loansAdvances')}
      description={t('banking.loans.description')}
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
          <ViewGridCard
            to={ROUTES.BANKING_LOANS_PRODUCT_WISE}
            title=""
            description=""
            metricLabel=""
            metricValue=""
            trend={{ direction: 'flat', text: '' }}
            loading
          />
          <ViewGridCard
            to={ROUTES.BANKING_LOANS_INTEREST_RATE_WISE}
            title=""
            description=""
            metricLabel=""
            metricValue=""
            trend={{ direction: 'flat', text: '' }}
            loading
          />
        </div>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const topProduct = loanProductMix(data, fy, branchId)[0]!
  const topSlab = [...loanRateSlabs(data, fy, branchId)].sort((a, b) => b.sharePct - a.sharePct)[0]!
  const advancesGrowth = yoyGrowth(fy, (f) => core(data, f, branchId).advances)

  return (
    <div className="flex flex-col gap-4">
      {header}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
        <ViewGridCard
          to={ROUTES.BANKING_LOANS_PRODUCT_WISE}
          title={t('banking.loans.productWise.title')}
          description={t('banking.loans.productWise.description')}
          metricLabel={t('banking.loans.productWise.metricLabel', { product: topProduct.label })}
          metricValue={`${topProduct.sharePct.toFixed(2)}%`}
          trend={{
            direction: trendLabel(advancesGrowth),
            text:
              advancesGrowth !== null
                ? t('banking.loans.bookYoyTrend', { value: formatSignedPct(advancesGrowth) })
                : t('banking.baseYear'),
          }}
        />
        <ViewGridCard
          to={ROUTES.BANKING_LOANS_INTEREST_RATE_WISE}
          title={t('banking.loans.interestRateWise.title')}
          description={t('banking.loans.interestRateWise.description')}
          metricLabel={t('banking.loans.interestRateWise.metricLabel')}
          metricValue={`${topSlab.sharePct.toFixed(2)}%`}
          trend={{ direction: 'flat', text: topSlab.label }}
        />
      </div>
    </div>
  )
}

export default BankingLoansPage
